import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { isSameDay, QuickDayOption, recentDayOptions } from './date-range.helpers';

export interface DateRange {
  start: NgbDateStruct | null;
  end: NgbDateStruct | null;
}

/**
 * Liste ekranlarının tarih aralığı süzgeci: son 7 günden tek gün seçilir ya da
 * "Özel" açılıp başlangıç ve bitiş ayrı ayrı girilir.
 *
 *   <app-date-range-filter [(start)]="filterStart" [(end)]="filterEnd"
 *                          (rangeChange)="reload()"></app-date-range-filter>
 *
 * Hızlı gün iki ucu birden değiştirir; sayfa yenilemeyi startChange/endChange
 * yerine rangeChange'e bağlamalı, yoksa liste iki kez yüklenir. Çift yönlü
 * bağlar rangeChange'ten önce yazıldığı için olay anında alanlar günceldir.
 */
@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.scss'],
})
export class DateRangeFilterComponent implements OnChanges {
  @Input() start: NgbDateStruct | null = null;
  @Input() end: NgbDateStruct | null = null;
  @Output() startChange = new EventEmitter<NgbDateStruct | null>();
  @Output() endChange = new EventEmitter<NgbDateStruct | null>();
  /** Kullanıcı aralığı değiştirdi; her seçimde bir kez. */
  @Output() rangeChange = new EventEmitter<DateRange>();

  /**
   * Alan, ngFor'a her değişiklik algılamada yeni dizi vermesin diye getter
   * değil: animasyonlu sayfalarda bu döngü sayfayı kilitliyor.
   */
  readonly quickDays: QuickDayOption[] = recentDayOptions();

  customOpen = false;

  ngOnChanges(changes: SimpleChanges): void {
    // Kayıtlı süzgeçten gelen ya da son 7 günün dışında kalan aralık hızlı
    // düğmelerle gösterilemez; alanlar açılmazsa uygulanan süzgeç görünmez kalır.
    if ((changes.start || changes.end) && this.hasValue && !this.activeQuickDay) {
      this.customOpen = true;
    }
  }

  get hasValue(): boolean {
    return !!this.start || !!this.end;
  }

  /** Aralık tek bir hızlı güne denk geliyorsa o gün. */
  get activeQuickDay(): QuickDayOption | null {
    return this.quickDays.find(option => this.isQuickDayActive(option.date)) ?? null;
  }

  isQuickDayActive(day: NgbDateStruct): boolean {
    return isSameDay(this.start, day) && isSameDay(this.end, day);
  }

  /** Seçili güne yeniden basmak süzgeci kaldırır. */
  selectQuickDay(day: NgbDateStruct): void {
    if (this.isQuickDayActive(day)) {
      this.apply(null, null);
      return;
    }
    this.customOpen = false;
    this.apply({ ...day }, { ...day });
  }

  /**
   * Özel aralık kapatılınca girilen tarihler de bırakılır: alanlar gizlenip
   * hiçbir düğme seçili görünmezken süzgeç sessizce uygulanmaya devam etmesin.
   * Hızlı gün seçiliyken açıp kapatmak aralığa dokunmaz.
   */
  toggleCustom(): void {
    if (this.customOpen && this.hasValue && !this.activeQuickDay) {
      this.apply(null, null);
    }
    this.customOpen = !this.customOpen;
  }

  onStartChange(value: NgbDateStruct | string | null): void {
    if (this.isPartial(value)) return;
    this.apply(value as NgbDateStruct | null, this.end);
  }

  onEndChange(value: NgbDateStruct | string | null): void {
    if (this.isPartial(value)) return;
    this.apply(this.start, value as NgbDateStruct | null);
  }

  clear(): void {
    this.apply(null, null);
  }

  private apply(start: NgbDateStruct | null, end: NgbDateStruct | null): void {
    this.start = start;
    this.end = end;
    this.startChange.emit(start);
    this.endChange.emit(end);
    this.rangeChange.emit({ start, end });
  }

  /**
   * Datepicker tarihe çevrilemeyen metni (yazılmakta olan "18.1") olduğu gibi
   * gönderiyor. Yok sayılır: süzgeç null'a çekilseydi geri yazılan model
   * kutuyu kullanıcı yazarken temizlerdi. Kutu boşaltılınca null gelir.
   */
  private isPartial(value: NgbDateStruct | string | null): boolean {
    return typeof value === 'string';
  }
}
